import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { getUserByUsername } from '../../utils/username';
import { completeCheckoutSession } from '../../utils/stripe';
import AvailabilityDisplay from './AvailabilityDisplay';
import SendMessageModal from '../Messaging/SendMessageModal';
import SessionBookingModal from '../Booking/SessionBookingModal';

const ProviderProfileView = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [userTimezone, setUserTimezone] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const checkoutProcessedRef = useRef(false);

  useEffect(() => {
    if (username) {
      fetchProviderProfile();
    }
    
    if (currentUser) {
      fetchCurrentUserData();
    }
    
    // Detect user's timezone
    try {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezone(detectedTimezone);
    } catch (error) {
      console.error('Could not detect user timezone:', error);
    }

    // Handle Stripe checkout success callback (only once)
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSessionId = urlParams.get('session_id');
    
    if (checkoutSessionId && currentUser && !checkoutProcessedRef.current) {
      checkoutProcessedRef.current = true;
      handleCheckoutSuccess(checkoutSessionId);
    }
  }, [username, currentUser]);

  const handleCheckoutSuccess = async (checkoutSessionId) => {
    setCheckoutLoading(true);
    try {
      const result = await completeCheckoutSession(checkoutSessionId);
      setBookingResult(result);
      setShowBookingModal(true);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Redirect to practitioner sessions after a delay
      setTimeout(() => {
        navigate('/practitioner-sessions');
      }, 3000);
    } catch (error) {
      console.error('Error completing checkout:', error);
      alert('Error completing booking. Please contact support.');
      // Reset the ref on error so user can try again
      checkoutProcessedRef.current = false;
    } finally {
      setCheckoutLoading(false);
    }
  };

  const fetchProviderProfile = async () => {
    try {
      // Try to get user by username first
      let providerData = await getUserByUsername(username);
      
      // If no user found by username, try as UID for backward compatibility
      if (!providerData) {
        const docRef = doc(db, 'users', username);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          providerData = { id: docSnap.id, ...docSnap.data() };
        }
      }
      
      if (providerData) {
        if (providerData.userType === 'provider' || providerData.userType === 'both') {
          setProvider(providerData);
          // Fetch reviews after getting provider data
          fetchProviderReviews(providerData.id);
        } else {
          // Not a provider, redirect back
          navigate('/providers');
        }
      } else {
        // Provider not found, redirect back
        navigate('/providers');
      }
    } catch (error) {
      console.error('Error fetching provider profile:', error);
      navigate('/providers');
    } finally {
      setLoading(false);
    }
  };

  const fetchProviderReviews = async (providerId) => {
    try {
      // Mock reviews for now - in a real app you'd fetch from a reviews collection
      const mockReviews = [
        {
          id: '1',
          reviewerName: 'John D.',
          rating: 5,
          comment: 'Excellent session! Really helped me improve my interview skills.',
          date: '2024-01-15'
        },
        {
          id: '2',
          reviewerName: 'Sarah M.',
          rating: 4,
          comment: 'Great communication skills and very patient. Learned a lot about public speaking.',
          date: '2024-01-10'
        },
        {
          id: '3',
          reviewerName: 'Mike R.',
          rating: 5,
          comment: 'Professional and knowledgeable. Would definitely book again.',
          date: '2024-01-05'
        }
      ];
      
      setReviews(mockReviews);
      
      // Calculate average rating
      if (mockReviews.length > 0) {
        const sum = mockReviews.reduce((acc, review) => acc + review.rating, 0);
        setAverageRating((sum / mockReviews.length).toFixed(1));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentUserData = async () => {
    if (!currentUser) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        setCurrentUserData({ id: userDoc.id, ...userDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching current user data:', error);
    }
  };

  const handleBookSession = () => {
    if (!currentUser) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    } else {
      setShowBookingModal(true);
    }
  };

  const handleSendMessage = () => {
    if (!currentUser) {
      const currentPath = window.location.pathname + window.location.search;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
    } else {
      setShowMessageModal(true);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<span key={i} className="star full">★</span>);
    }

    if (hasHalfStar) {
      stars.push(<span key="half" className="star half">★</span>);
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<span key={`empty-${i}`} className="star empty">☆</span>);
    }

    return stars;
  };

  if (loading) {
    return <div className="loading">Loading provider profile...</div>;
  }

  if (!provider) {
    return <div className="error">Provider not found</div>;
  }

  return (
    <div className="provider-profile-view">
      <button className="back-button" onClick={() => navigate('/providers')}>
        ← Back to Providers
      </button>

      <div className="provider-header">
        <div className="provider-image">
          {provider.profilePicture && provider.profilePicture.trim() !== '' ? (
            <img 
              src={provider.profilePicture} 
              alt={`${provider.firstName} ${provider.lastName}`}
              onError={(e) => {
                console.log('Profile view image failed to load:', provider.profilePicture);
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
              onLoad={() => {
                console.log('Profile view image loaded successfully:', provider.profilePicture);
              }}
            />
          ) : null}
          <div className="default-avatar" style={{ display: provider.profilePicture && provider.profilePicture.trim() !== '' ? 'none' : 'flex' }}>
            {provider.firstName?.[0]}{provider.lastName?.[0]}
          </div>
        </div>
        
        <div className="provider-basic-info">
          <h1>{provider.firstName} {provider.lastName}</h1>
          <div className="rating-summary">
            <div className="stars">{renderStars(parseFloat(averageRating))}</div>
            <span className="rating-text">
              {averageRating} ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
            </span>
          </div>
          <div className="hourly-rate">${provider.hourlyRate}/hour</div>
          {provider.timezone && (
            <div className="provider-timezone-info">
              <p className="timezone-location">🌍 {provider.timezone.replace('_', ' ')}</p>
              <p className="current-time">
                Local time: {(() => {
                  try {
                    // Handle legacy Mumbai timezone
                    const timezone = provider.timezone === 'Asia/Mumbai' ? 'Asia/Kolkata' : provider.timezone;
                    return new Date().toLocaleString('en-US', { 
                      timeZone: timezone,
                      weekday: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });
                  } catch (error) {
                    return 'Time unavailable';
                  }
                })()}
              </p>
            </div>
          )}
        </div>

        <div className="provider-actions">
          <button className="book-session-btn primary" onClick={handleBookSession}>
            {currentUser ? 'Book Session' : 'Sign In to Book'}
          </button>
          <button className="send-message-btn secondary" onClick={handleSendMessage}>
            {currentUser ? 'Send Message' : 'Sign In to Message'}
          </button>
        </div>
      </div>

      {/* Availability Section - Full Width */}
      <section className="availability-section-full">
        <div className="availability-header">
          <h2>Availability</h2>
          <p>Select a time slot to book a session with {provider.firstName}</p>
        </div>
        <AvailabilityDisplay 
          provider={provider} 
          userTimezone={userTimezone}
        />
      </section>

      <div className="provider-content">
        <div className="provider-main">
          <section className="bio-section">
            <h2>About</h2>
            <p>{provider.bio || 'No bio available.'}</p>
          </section>

          {provider.experience && (
            <section className="experience-section">
              <h2>Experience</h2>
              <p>{provider.experience}</p>
            </section>
          )}

          <section className="reviews-section">
            <h2>Reviews</h2>
            {reviews.length === 0 ? (
              <p>No reviews yet.</p>
            ) : (
              <div className="reviews-list">
                {reviews.map(review => (
                  <div key={review.id} className="review-card">
                    <div className="review-header">
                      <span className="reviewer-name">{review.reviewerName}</span>
                      <div className="review-rating">{renderStars(review.rating)}</div>
                      <span className="review-date">{new Date(review.date).toLocaleDateString()}</span>
                    </div>
                    <p className="review-comment">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="provider-sidebar">
          {provider.skills && provider.skills.length > 0 && (
            <section className="skills-section">
              <h3>Skills</h3>
              <div className="tags">
                {provider.skills.map(skill => (
                  <span key={skill} className="tag skill-tag">{skill}</span>
                ))}
              </div>
            </section>
          )}

          {provider.expertise && provider.expertise.length > 0 && (
            <section className="expertise-section">
              <h3>Expertise</h3>
              <div className="tags">
                {provider.expertise.map(area => (
                  <span key={area} className="tag expertise-tag">{area}</span>
                ))}
              </div>
            </section>
          )}

          {provider.languages && provider.languages.length > 0 && (
            <section className="languages-section">
              <h3>Languages</h3>
              <div className="tags">
                {provider.languages.map(lang => (
                  <span key={lang} className="tag language-tag">{lang}</span>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
      
      <SendMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        recipient={provider}
        currentUserData={currentUserData}
      />

      {showBookingModal && (
        <SessionBookingModal
          provider={provider}
          currentUser={currentUser}
          onClose={() => {
            setShowBookingModal(false);
            setBookingResult(null);
          }}
          bookingResult={bookingResult}
          initialStep={bookingResult ? 'success' : 'details'}
        />
      )}
      
      {checkoutLoading && (
        <div className="checkout-loading-overlay">
          <div className="checkout-loading-content">
            <div className="loading-icon">⏳</div>
            <h3>Processing your booking...</h3>
            <p>Please wait while we complete your session booking.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderProfileView;